<template>
  <section class="section">
    <div class="container">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><a @click.prevent="$router.back()">← Back</a></li>
          <li class="is-active"><a aria-current="page">Item detail</a></li>
        </ul>
      </nav>

      <div class="level">
        <div class="level-left">
          <h1 class="title is-4">{{ item?.name || 'Item' }}</h1>
          <span
            v-if="item && item.is_active === false"
            class="tag is-warning ml-2"
          >
            Archived
          </span>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button
              class="button"
              @click="refresh"
              :class="{ 'is-loading': loading }"
            >
              Refresh
            </button>
            <button
              class="button"
              :class="item?.is_active ? 'is-danger' : 'is-success'"
              :disabled="loadingArchive || !item"
              @click="toggleArchive"
            >
              {{ item?.is_active ? 'Archive' : 'Unarchive' }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="error" class="notification is-danger is-light">
        <button class="delete" @click="error = ''"></button>
        {{ error }}
      </div>

      <div v-if="loading" class="box">Loading…</div>

      <div v-else-if="item" class="columns is-variable is-5">
        <!-- Left: main info -->
        <div class="column is-7">
          <div class="box">
            <div class="mb-2">
              <span
                :class="[
                  'tag',
                  item.type === 'product' ? 'is-link' : 'is-info',
                ]"
                >{{ item.type_display || item.type }}</span
              >
              <span
                v-if="item.type !== 'service'"
                :class="['tag', statusTag(item), 'ml-2']"
                >{{ statusText(item) }}</span
              >
            </div>

            <p v-if="item.description" class="is-size-6">
              {{ item.description }}
            </p>

            <div class="columns is-mobile mt-3">
              <div class="column">
                <p class="has-text-grey is-size-7">Current stock</p>
                <p class="is-size-5">{{ item.current_stock ?? '—' }}</p>
              </div>
              <div class="column">
                <p class="has-text-grey is-size-7">Min stock</p>
                <p class="is-size-5">{{ item.minimum_stock }}</p>
              </div>
              <div class="column">
                <p class="has-text-grey is-size-7">Unit price</p>
                <p class="is-size-5">{{ formatCurrency(item.unit_price) }}</p>
              </div>
            </div>
          </div>

          <!-- Optional: actions (kept simple to match your style) -->
          <div class="buttons">
            <button
              class="button"
              :disabled="item.type === 'service'"
              @click="openMovement(+1)"
            >
              IN
            </button>
            <button
              class="button is-warning"
              :disabled="
                item.type === 'service' || (item.current_stock || 0) <= 0
              "
              @click="openMovement(-1)"
            >
              OUT
            </button>
          </div>
          <AnalyticsPicker :objectId="item.id" scope="item" endpoint="/items" />
        </div>

        <!-- Right: recent movements -->
        <div class="column is-5">
          <div class="box">
            <div
              class="is-flex is-justify-content-space-between is-align-items-center"
            >
              <h2 class="title is-6">Recent movements</h2>
              <button
                class="button is-small"
                @click="fetchMovements"
                :class="{ 'is-loading': loadingMovements }"
              >
                Reload
              </button>
            </div>

            <div v-if="movError" class="notification is-danger is-light">
              <button class="delete" @click="movError = ''"></button>
              {{ movError }}
            </div>

            <div v-if="loadingMovements">Loading…</div>
            <div v-else>
              <table
                class="table is-fullwidth is-striped is-hoverable is-size-7"
              >
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th class="has-text-right">Qty</th>
                    <th>By</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in movements" :key="m.id">
                    <td>{{ formatDate(m.created_at) }}</td>
                    <td>
                      <span
                        :class="[
                          'tag',
                          m.coefficient === 1 ? 'is-success' : 'is-warning',
                        ]"
                      >
                        {{
                          m.coefficient_display ||
                          (m.coefficient === 1 ? 'IN' : 'OUT')
                        }}
                      </span>
                    </td>
                    <td class="has-text-right">{{ m.quantity }}</td>
                    <td>{{ m.created_by?.username || '—' }}</td>
                    <td class="truncate">{{ m.comment || '—' }}</td>
                  </tr>
                  <tr v-if="!movements.length">
                    <td colspan="5" class="has-text-centered has-text-grey">
                      No movements.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Movement modal (minimal) -->
      <div class="modal" :class="{ 'is-active': ui.movementOpen }">
        <div class="modal-background" @click="ui.movementOpen = false"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Stock movement — {{ movement.coefficient === 1 ? 'IN' : 'OUT' }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="ui.movementOpen = false"
            ></button>
          </header>
          <section class="modal-card-body">
            <div class="field">
              <label class="label">Quantity</label>
              <div class="control">
                <input
                  v-model.number="movement.quantity"
                  type="number"
                  min="1"
                  class="input"
                />
              </div>
            </div>
            <div class="field">
              <label class="label">Comment</label>
              <div class="control">
                <input
                  v-model="movement.comment"
                  type="text"
                  class="input"
                  placeholder="Optional"
                />
              </div>
            </div>
            <p v-if="movPostError" class="help is-danger">{{ movPostError }}</p>
          </section>
          <footer class="modal-card-foot">
            <button class="button" @click="ui.movementOpen = false">
              Cancel
            </button>
            <button
              class="button is-primary"
              :class="{ 'is-loading': postingMovement }"
              :disabled="!validMovement"
              @click="saveMovement"
            >
              Save
            </button>
          </footer>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/utils/axios';
import AnalyticsPicker from '@/views/analytics/AnalyticsPicker.vue';

export default {
  components: { AnalyticsPicker },
  name: 'ItemDetail',
  data() {
    return {
      item: null,
      loading: false,
      error: '',

      movements: [],
      loadingMovements: false,
      movError: '',

      ui: { movementOpen: false },
      movement: { coefficient: 1, quantity: 1, comment: '' },
      movPostError: '',
      postingMovement: false,
      loadingArchive: false,
    };
  },
  created() {
    this.refresh();
  },
  computed: {
    validMovement() {
      return (
        Number.isInteger(this.movement.quantity) && this.movement.quantity > 0
      );
    },
  },
  methods: {
    async refresh() {
      await this.fetchItem();
      await this.fetchMovements();
    },
    async fetchItem() {
      this.loading = true;
      this.error = '';
      try {
        const id = this.$route.params.id;
        const { data } = await api.get(`/items/${id}/`);
        this.item = data;
      } catch (e) {
        this.error =
          e?.response?.data?.detail || e?.message || 'Failed to load item.';
      } finally {
        this.loading = false;
      }
    },
    async fetchMovements() {
      this.loadingMovements = true;
      this.movError = '';
      try {
        const id = this.$route.params.id;
        const { data } = await api.get('/stock-movements/', {
          params: { item: id, page_size: 10 },
        });
        this.movements = Array.isArray(data) ? data : data.results || data;
      } catch (e) {
        this.movError =
          e?.response?.data?.detail ||
          e?.message ||
          'Failed to load movements.';
      } finally {
        this.loadingMovements = false;
      }
    },
    async toggleArchive() {
      if (!this.item) return;
      // Confirm only when archiving (not required when unarchiving)
      const willArchive = !!this.item.is_active;
      if (willArchive) {
        const ok = window.confirm(
          'Archive this item?\nIt will be hidden from lists but kept for history.'
        );
        if (!ok) return;
      }

      this.loadingArchive = true;
      this.error = '';
      try {
        // Patch only the flag; DRF ModelViewSet supports partial update by default
        const { data } = await api.patch(`/items/${this.item.id}/`, {
          is_active: !this.item.is_active,
        });

        // In case API returns partial or full object, merge safely
        this.item = {
          ...this.item,
          ...(data || {}),
          is_active: !this.item.is_active,
        };
      } catch (e) {
        this.error =
          e?.response?.data?.detail ||
          e?.message ||
          'Failed to update item status.';
      } finally {
        this.loadingArchive = false;
      }
    },
    statusTag(item) {
      if (item.type === 'service') return 'is-light';
      const q = item.current_stock ?? 0;
      if (q <= 0) return 'is-danger';
      if (item.minimum_stock && q <= item.minimum_stock) return 'is-warning';
      return 'is-success';
    },
    statusText(item) {
      if (item.type === 'service') return 'N/A';
      const q = item.current_stock ?? 0;
      if (q <= 0) return 'Out';
      if (item.minimum_stock && q <= item.minimum_stock) return 'Low';
      return 'OK';
    },
    formatCurrency(v) {
      const n = typeof v === 'string' ? Number(v) : v;
      return Number.isNaN(n)
        ? v
        : new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: 'EUR',
          }).format(n);
    },
    formatDate(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleString();
      } catch (_) {
        return iso;
      }
    },
    openMovement(coef) {
      this.movement.coefficient = coef;
      this.movement.quantity = 1;
      this.movement.comment = '';
      this.movPostError = '';
      this.ui.movementOpen = true;
    },
    async saveMovement() {
      if (!this.item) return;
      this.postingMovement = true;
      this.movPostError = '';
      try {
        await api.post('/stock-movements/', {
          item: this.item.id,
          coefficient: this.movement.coefficient,
          quantity: this.movement.quantity,
          comment: this.movement.comment,
        });
        this.ui.movementOpen = false;
        await this.refresh();
      } catch (e) {
        this.movPostError =
          e?.response?.data?.detail ||
          e?.message ||
          'Failed to create movement.';
      } finally {
        this.postingMovement = false;
      }
    },
  },
};
</script>

<style scoped>
.truncate {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
